import {
  Autocomplete,
  TextField,
  Box,
  Chip,
  Skeleton,
  Tooltip,
  InputAdornment,
  Button,
  Checkbox,
} from '@mui/material';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { usePartyManager } from './PartyManagerContext';
import {
  POSSIBLE_SOURCES,
  EDIT_SOURCE,
  CODEMETA_SOURCE,
} from './partyManagerUtils';

const OrganizationAssignmentSelector = ({ label }) => {
  const {
    partyData,
    isUIBlocked,
    isPartyManagerLoading,
    handleOrganizationAssignmentChange,
    handleOrganizationContactToggle,
    handleTransferOrganizationsToEdit,
    handleCycleSource,
    getAvailableSources,
    contactWarnings,
    organizationOptions,
    isSearchingOrgs,
    handleOrganizationSearch,
    selectedOrgValue,
  } = usePartyManager();

  const fieldKey = 'assignedOrganizations';
  const fieldState = partyData[fieldKey];

  if (!fieldState) {
    return null;
  }

  const selectedSource = fieldState.selected || 'edit';
  const isLocked = selectedSource !== 'edit';
  const availableSources = getAvailableSources(fieldKey);

  const getButtonIcon = () => {
    if (selectedSource === 'edit') return EDIT_SOURCE.icon;
    if (selectedSource === 'codemeta') {
      return (
        <img
          src={CODEMETA_SOURCE.icon}
          alt={CODEMETA_SOURCE.name}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }
    const sourceInfo = POSSIBLE_SOURCES[selectedSource];
    if (sourceInfo?.icon) {
      return (
        <img
          src={sourceInfo.icon}
          alt={sourceInfo.name}
          style={{ width: '24px', height: '24px' }}
        />
      );
    }
    return '?';
  };

  const selectedAssignments = fieldState[selectedSource] || [];
  const value = selectedOrgValue;

  const warnings = Object.values(contactWarnings).flat();
  const warningTitle =
    warnings.length > 0 ? (
      <div>
        <p>Folgende Personen wurden nicht gefunden:</p>
        <ul>
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      </div>
    ) : (
      ''
    );

  const tooltipTitle = isLocked
    ? 'Klicken, um diese Auswahl zur Bearbeitung zu übernehmen.'
    : '';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        width: '100%',
      }}
    >
      {isPartyManagerLoading ? (
        <Skeleton variant="rounded" width="100%" height={56} />
      ) : (
        <>
          <Tooltip title={tooltipTitle} placement="top-start" arrow>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                cursor: isLocked ? 'pointer' : 'default',
              }}
              onClick={isLocked ? handleTransferOrganizationsToEdit : undefined}
            >
              <Autocomplete
                multiple
                id="organization-assignment-select"
                fullWidth
                options={organizationOptions}
                loading={isSearchingOrgs}
                value={value}
                onInputChange={(event, newInputValue) => {
                  handleOrganizationSearch(newInputValue);
                }}
                onChange={(event, newValue) => {
                  handleOrganizationAssignmentChange(newValue);
                }}
                filterOptions={(x) => x}
                isOptionEqualToValue={(option, val) => option._id === val._id}
                getOptionLabel={(option) =>
                  option.company
                    ? `${option.company}${option?.customerNumber ? ` [${option.customerNumber}]` : option?.supplierNumber ? ` [${option.supplierNumber}]` : ''}`
                    : `${option.firstName} ${option.lastName}`.trim()
                }
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => {
                    const isContact = selectedAssignments.find(
                      (a) => a.id === option._id,
                    )?.contact;
                    const { key: _omitKey, ...chipTagProps } = getTagProps({
                      index,
                    });
                    return (
                      <Chip
                        key={option._id}
                        {...chipTagProps}
                        variant="outlined"
                        size="small"
                        label={
                          option.company
                            ? `${option.company}${option?.customerNumber ? ` [${option.customerNumber}]` : option?.supplierNumber ? ` [${option.supplierNumber}]` : ''}`
                            : `${option.firstName} ${option.lastName}`.trim()
                        }
                        onDelete={() => {
                          const newValue = value.filter(
                            (v) => v._id !== option._id,
                          );
                          handleOrganizationAssignmentChange(newValue);
                        }}
                        icon={
                          <Tooltip title="Als Ansprechpartner markieren">
                            <Checkbox
                              size="small"
                              icon={
                                <ContactPhoneOutlinedIcon color="disabled" />
                              }
                              checkedIcon={
                                <ContactPhoneOutlinedIcon color="primary" />
                              }
                              checked={!!isContact}
                              onChange={() =>
                                handleOrganizationContactToggle(option._id)
                              }
                              sx={{ p: 0, mr: 0.5 }}
                              disabled={
                                isUIBlocked || selectedSource !== 'edit'
                              }
                            />
                          </Tooltip>
                        }
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={label}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {warnings.length > 0 && (
                            <InputAdornment position="end">
                              <Tooltip title={warningTitle}>
                                <WarningAmberIcon color="warning" />
                              </Tooltip>
                            </InputAdornment>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: 'rgba(0, 0, 0, 0.87)',
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(0, 0, 0, 0.38)',
                        '&.MuiInputLabel-shrink': {
                          color: 'rgba(0, 0, 0, 0.87)',
                        },
                      },
                    }}
                  />
                )}
                disabled={isUIBlocked || isLocked}
                sx={{ flex: 1, minWidth: 0 }}
              />
            </Box>
          </Tooltip>
          {availableSources.length > 1 && (
            <Button
              title={`Quelle wechseln: Aktuell ${selectedSource}.`}
              sx={{ width: '40px', height: '40px', minWidth: '40px' }}
              onClick={() => handleCycleSource(fieldKey)}
              variant="outlined"
            >
              {getButtonIcon()}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default OrganizationAssignmentSelector;
